import { Box, Skeleton } from '@mui/material';
import { colorTokens, radiusTokens, spacingTokens } from '../app/designTokens';

const { semantic } = colorTokens;

const BAR_SX = {
  bgcolor: semantic.action.secondary.bg,
  borderRadius: `${radiusTokens.sm}px`,
  transform: 'none',
  '::after': {
    background: `linear-gradient(90deg, transparent, ${semantic.border.subtle}, transparent)`,
  },
};

function SkeletonBar({ width = '100%', height = 12, sx }) {
  return (
    <Skeleton
      variant="rounded"
      animation="wave"
      width={width}
      height={height}
      sx={{ ...BAR_SX, ...sx }}
    />
  );
}

function TableSkeleton({ rows, columns, embedded }) {
  const widths = ['12%', '22%', '18%', '24%', '14%', '10%', '8%'];

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label="Loading"
      sx={{
        bgcolor: embedded ? 'transparent' : semantic.bg.surface,
        border: embedded ? 'none' : `1px solid ${semantic.border.subtle}`,
        borderRadius: embedded ? 0 : `${radiusTokens.card}px`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2.5,
          py: 1.5,
          bgcolor: semantic.bg.input,
          borderBottom: `1px solid ${semantic.border.default}`,
        }}
      >
        {Array.from({ length: columns }).map((_, index) => (
          <SkeletonBar
            key={`h-${index}`}
            width={widths[index % widths.length]}
            height={10}
            sx={{ flex: index === 1 ? '1 1 auto' : '0 1 auto', maxWidth: 160 }}
          />
        ))}
      </Box>

      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box
          key={`r-${rowIndex}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2.5,
            py: 1.75,
            borderBottom:
              rowIndex === rows - 1
                ? 'none'
                : `1px solid ${semantic.border.subtle}`,
          }}
        >
          {Array.from({ length: columns }).map((__, colIndex) => (
            <SkeletonBar
              key={`c-${rowIndex}-${colIndex}`}
              width={widths[colIndex % widths.length]}
              height={12}
              sx={{
                flex: colIndex === 1 ? '1 1 auto' : '0 1 auto',
                maxWidth: colIndex === 1 ? 280 : 160,
                opacity: 0.85 - (rowIndex % 3) * 0.08,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

function DetailSkeleton() {
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label="Loading"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          p: spacingTokens.cardPadding / spacingTokens.unit,
          bgcolor: semantic.bg.surface,
          border: `1px solid ${semantic.border.subtle}`,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        <SkeletonBar width="40%" height={18} sx={{ mb: 1.5 }} />
        <SkeletonBar width="70%" height={12} sx={{ mb: 1 }} />
        <SkeletonBar width="55%" height={12} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        {[0, 1].map((index) => (
          <Box
            key={index}
            sx={{
              p: spacingTokens.cardPadding / spacingTokens.unit,
              bgcolor: semantic.bg.surface,
              border: `1px solid ${semantic.border.subtle}`,
              borderRadius: `${radiusTokens.card}px`,
            }}
          >
            <SkeletonBar width="45%" height={14} sx={{ mb: 2 }} />
            <SkeletonBar width="100%" height={12} sx={{ mb: 1 }} />
            <SkeletonBar width="88%" height={12} sx={{ mb: 1 }} />
            <SkeletonBar width="72%" height={12} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function CardGridSkeleton({ rows }) {
  const cards = Math.max(rows * 2, 4);

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label="Loading"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: '1fr 1fr',
          md: '1fr 1fr 1fr',
        },
        gap: 2,
      }}
    >
      {Array.from({ length: cards }).map((_, index) => (
        <Box
          key={index}
          sx={{
            p: spacingTokens.cardPadding / spacingTokens.unit,
            bgcolor: semantic.bg.surface,
            border: `1px solid ${semantic.border.subtle}`,
            borderRadius: `${radiusTokens.card}px`,
            minHeight: 120,
          }}
        >
          <SkeletonBar width="50%" height={14} sx={{ mb: 2 }} />
          <SkeletonBar width="100%" height={12} sx={{ mb: 1 }} />
          <SkeletonBar width="80%" height={12} />
        </Box>
      ))}
    </Box>
  );
}

/**
 * Shared App / LoadingSkeleton (Figma Composite).
 * @param {'Table'|'Detail'|'CardGrid'} [type='Table']
 * @param {number} [rows=6] - Placeholder row/card density
 * @param {number} [columns=5] - Table header/body column count
 * @param {boolean} [embedded] - Table only: omit outer card chrome when nested in SectionCard
 */
export function LoadingSkeleton({
  type = 'Table',
  rows = 5,
  columns = 5,
  embedded = false,
  sx,
}) {
  let content = null;

  if (type === 'Detail') {
    content = <DetailSkeleton />;
  } else if (type === 'CardGrid') {
    content = <CardGridSkeleton rows={rows} />;
  } else {
    content = (
      <TableSkeleton
        rows={rows}
        columns={Math.max(columns, 1)}
        embedded={embedded}
      />
    );
  }

  return (
    <Box data-loading-skeleton-type={type} sx={sx}>
      {content}
    </Box>
  );
}

export default LoadingSkeleton;
